import {
  PickupLocationStylesDescriptor,
  PickupTimeStylesDescriptor,
  ProductDetailsStylesDescriptor,
  MenuListStylesDescriptor,
  FlashListPropsType,
  Identifiable,
  ProductComponentProps,
  AddItemButtonProps,
  UpsellButtonProps,
  DepositStylesDescriptor,
} from './DescriptorType';
import Styles from 'src/components/Styles';
import {
  StyleSheet,
  View,
  Image,
  Platform,
  PixelRatio,
  Text,
  FlatList,
} from 'react-native';
import Localized from 'src/constants/AppStrings';
import {ensureHttps} from 'src/services/StringUtils';
import AVText from 'src/components/elements/AVText';
import {MenuProductType} from 'src/types/Menu';
import {DNA} from 'src/types/dnaTypes';
import {TransactionDetail} from 'src/types/TransactionDetail';
import {ListScanItemsProps} from 'src/components/screens/scanAndPay/listScanItems/ListScanItems';
import {
  DietaryHealthGood,
  DietaryHealthWarning,
  DietaryHealthAlert,
} from 'src/components/img/svg/DietaryHealthGrades';
import AVFormattedCurrency from 'src/components/elements/AVFormattedCurrency';
import TagLightIcon from 'src/components/img/svg/TagLightIcon';
import AccountStore from 'src/stores/AccountStore';
import RecentComboIcon from 'src/components/img/svg/RecentComboIcon';
import MenuItemListTemplate2 from 'src/components/elements/orderAhead/MenuListItemTemplate2';
import Util from 'src/Util';
import DashedLine from 'react-native-dashed-line';
import {BarButton} from 'src/components/elements/orderAhead/BarButton';
import TransactionUpsellItem from 'src/components/elements/orderAhead/TransactionalUpsell';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';

export function getPickupLocationStyles(): PickupLocationStylesDescriptor {
  return StyleSheet.create({
    buttonContainerStyle: {
      marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
      marginBottom: Styles.Spacing.m3 + Styles.Spacing.m2,
      marginTop: Styles.Spacing.m3,
      alignSelf: 'center',
      width: Platform.OS === 'web' ? '100%' : null,
    },
    buttonText: {
      fontFamily: Styles.FontFamily.robotoRegular,
      fontSize: Styles.Fonts.f1,
      fontWeight: '700',
    },
    headerText: {
      fontWeight: '700',
      fontSize: Styles.Fonts.f7,
      color: Styles.darkColor,
      marginBottom: Styles.Spacing.m1,
      fontFamily: Styles.FontFamily.robotoRegular,
    },
    errorText: {
      fontWeight: '700',
      fontSize: Styles.Fonts.f7,
      color: Styles.Colors.PayNew.error,
      paddingHorizontal: Styles.Spacing.m1,
      alignItems: 'center',
      marginBottom: 1,
      fontFamily: Styles.FontFamily.robotoRegular,
    },
    errorContainer: {
      flexDirection: 'row',
      paddingTop: Styles.Spacing.m1,
      alignItems: 'center',
    },
    containerStyle: {
      width: Platform.OS === 'web' ? '100%' : Styles.ScreenWidth,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    },
    buttonView: {
      width: '100%',
    },
    upsellPointsView: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'baseline',
      paddingTop: 5,
    },
    upsellPointsText: {
      flex: 1,
      fontSize: Styles.Fonts.f8,
      fontWeight: '400',
      color: Styles.Colors.PayNew.darkHuesBase03,
      fontFamily: fontFamilyStyle(),
    },
  });
}

export function renderNutritionPromotionComponent(
  nutritionPromoName: string,
  nutritionDiscountAmount: number,
) {
  return (
    !isNaN(nutritionDiscountAmount) &&
    nutritionPromoName !== undefined &&
    nutritionPromoName !== '' && (
      <View style={styles.nutritionDiscountView}>
        <View
          style={styles.nutritionLabelView}
          testID={'qa_NutritionPromoLabel_txt'}
        >
          <TagLightIcon
            fill={Styles.Colors.Canteen.green}
            width={Styles.Spacing.m3 + 1}
            height={Styles.Spacing.m3 + 2}
          />
          <AVText
            testID={'qa_NutritionPromoLabel_txt'}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            style={styles.nutritionLabel}
          >
            {nutritionPromoName}
          </AVText>
        </View>

        <View testID={'qa_NutritionPromoValue_txt'}>
          <AVFormattedCurrency
            currency={AccountStore.getCurrency()}
            value={nutritionDiscountAmount * -1}
            style={styles.nutritionPromoAmount}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessibilityLabel={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}`}
            accessibilityRole="text"
            aria-label={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}, text`}
          />
        </View>
      </View>
    )
  );
}

export function renderColorNutritionComponent(
  nutritionDisplayColor: string,
  nutritionDisplayLetter: string,
  nutritionCalorieTotal: number,
) {
  let healthGradeIcon: JSX.Element | false;
  if (
    nutritionDisplayLetter?.toUpperCase() === 'G' &&
    nutritionDisplayColor?.toUpperCase() === 'GREEN'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: good, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: good, "
      >
        <DietaryHealthGood />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'Y' &&
    nutritionDisplayColor?.toUpperCase() === 'YELLOW'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: warning, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: warning, "
      >
        <DietaryHealthWarning />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'R' &&
    nutritionDisplayColor?.toUpperCase() === 'RED'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: alert, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: alert, "
      >
        <DietaryHealthAlert />
      </View>
    );
  }

  return (
    <View>
      <View>
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginLeft: -Styles.Spacing.m1,
            marginTop: Styles.Spacing.m3,
            marginBottom: Styles.Spacing.m2,
          }}
        >
          <View style={{padding: Styles.Spacing.m1}}>{healthGradeIcon}</View>
          <View
            style={{
              alignItems: 'flex-start',
            }}
          >
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              style={{
                marginBottom: Styles.Spacing.m1,
                color: '#22212D',
                fontSize: 18,
                fontFamily: Styles.FontFamily.robotoRegular,
                fontWeight: 'bold',
                alignItems: 'flex-start',
                paddingLeft: Styles.Spacing.m1,
              }}
            >
              {nutritionCalorieTotal}
            </AVText>
          </View>
          <View style={Styles.Style.labelColumn2}>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              style={{
                marginBottom: Styles.Spacing.m1,
                color: '#22212D',
                fontSize: 18,
                fontFamily: Styles.FontFamily.robotoRegular,
                fontWeight: 'bold',
                alignItems: 'flex-start',
                paddingLeft: Styles.Spacing.m1,
              }}
            >
              {this.props?.location?.locationFeatures?.dna
                ?.showCaloriesAsKilocalories
                ? Localized.Labels.total_kilocalories
                : Localized.Labels.total_calories}
            </AVText>
          </View>
        </View>
      </View>
    </View>
  );
}
export function getInactiveButtonColor() {
  return Styles.Colors.PayNew.darkHuesBase04;
}

export function contactPermissionErrorText() {
  return Localized.Labels.formatString(
    Localized.Labels.choose_contact_permission_error,
    Localized.Labels.app_name_canteen,
  );
}
export function fontFamilyStyle() {
  return Styles.FontFamily.robotoRegular;
}
export function calorieTextColor() {
  return Styles.Colors.PayNew.darkHuesBase03;
}

export function textColor() {
  return Styles.darkColor;
}
export function getPickupTimeStyles(): PickupTimeStylesDescriptor {
  return StyleSheet.create({
    dateView: {
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
      marginLeft: Styles.Spacing.m3,
    },
    timeView: {
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    },
    pickupTimeContainer: {
      flex: 1,
      marginTop: Styles.Heights.h2,
    },
    pickupLocationHeaderView: {
      flexDirection: 'row',
      width: '100%',
      minHeight: Styles.Heights.touchTargetHeight2,
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: Styles.Heights.h1,
      marginLeft: Styles.Spacing.m3,
    },
    customTileButtonTime: {
      minWidth: 105,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: Styles.Spacing.m1,
    },
    tilesStyle: {
      marginRight: Styles.Spacing.m3 - Styles.Spacing.m1,
      marginTop: Styles.Spacing.m3,
    },
    errorContainer: {
      flexDirection: 'row',
      marginTop: Styles.Spacing.m2,
      alignItems: 'center',
    },
    buttonTextStyle: {
      fontWeight: '700',
      fontSize: Styles.Fonts.f1,
      paddingHorizontal: Styles.Spacing.m1,
      marginBottom: 1,
      fontFamily: Styles.FontFamily.robotoRegular,
      color: Styles.Colors.Canteen.primary,
    },
    editButton: {
      flexDirection: 'row',
      alignItems: 'center',
      borderColor: Styles.Colors.Canteen.primary,
      borderRadius: 32,
      borderWidth: 1,
      paddingVertical: 5,
      paddingHorizontal: 8,
      marginRight: Styles.Spacing.m3,
      overflow: 'hidden',
    },
    dateText: {
      fontWeight: '700',
      fontSize: Styles.Fonts.f7,
      color: Styles.darkColor,
      fontFamily: fontFamilyStyle(),
    },
  });
}

export function getActiveButtonColor() {
  return Styles.primaryColor;
}
export function locationErrorMessage() {
  return Localized.Errors.please_select_location;
}
export function dayErrorMessage() {
  return Localized.Errors.please_select_day;
}
export function timeErrorMessage() {
  return Localized.Errors.please_select_time;
}

export function productDetailsTitle() {
  return Localized.Labels.item_info;
}

export function productDetailsImageView(imageUri: string | null): JSX.Element {
  return (
    <>
      <View>
        {imageUri ? (
          <Image
            accessible={true}
            accessibilityLabel="Product image"
            accessibilityRole="image"
            aria-label="Product image"
            role="img"
            style={{aspectRatio: 16 / 9}}
            source={{uri: ensureHttps(imageUri)}}
            resizeMode={'cover'}
          />
        ) : (
          <Image
            accessible={true}
            accessibilityLabel="Default Product image"
            accessibilityRole="image"
            aria-label="Default Product image"
            role="img"
            source={require('src/components/img/CPProductPlaceholder.png')}
            resizeMode={'cover'}
            style={{width: '100%', minHeight: 188}}
          />
        )}
      </View>
    </>
  );
}
export function getEmptyCartlblStyle() {
  return {
    fontFamily: Styles.FontFamily.robotoRegular,
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    textAlign: 'left',
    color: Styles.Colors.PayNew.black01,
  };
}
export function productDetailsStyles(): ProductDetailsStylesDescriptor {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    content: {
      marginVertical: Styles.Spacing.m1,
    },
    title: {
      fontWeight: '700',
      fontSize: Styles.Fonts.f7,
      color: Styles.Colors.PayNew.black01,
      marginBottom: Styles.Spacing.m2,
      fontFamily: Styles.FontFamily.robotoRegular,
    },
    infoContainer: {
      paddingHorizontal: Styles.Spacing.m3,
      paddingVertical: Styles.Spacing.m1,
      flexDirection: 'column',
    },
    productPrice: {
      fontWeight: '700',
      marginTop: 1,
      fontSize: Styles.Fonts.f1,
      color: Styles.Colors.PayNew.black01,
      fontFamily: Styles.FontFamily.robotoRegular,
    },
    calories: {
      fontWeight: '700',
      color: Styles.Colors.PayNew.black01,
      fontSize: Styles.Fonts.f1,
      fontFamily: Styles.FontFamily.robotoRegular,
      marginTop: 1,
    },
    calorieText: {
      fontWeight: '700',
      color: Styles.Colors.PayNew.black01,
      fontSize: Styles.Fonts.f1,
      fontFamily: Styles.FontFamily.robotoRegular,
      marginLeft: Styles.Spacing.m1,
      marginTop: 1,
    },
    healthGrade: {
      marginLeft: Styles.Spacing.m2,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: Styles.Spacing.m1,
    },
    verticalLine: {
      marginHorizontal: Styles.Spacing.m2,
      backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
      borderColor: Styles.Colors.PayNew.darkHuesBase04,
      borderWidth: 0.8,
    },
    nutritionButtonView: {
      flexDirection: 'row',
      marginVertical: Styles.Spacing.m2,
    },
    outlined: {
      flexDirection: 'row',
      justifyContent: 'center',
      borderRadius: Styles.Spacing.m4,
      borderColor: Styles.Colors.Canteen.primary,
      marginTop: Styles.Spacing.m2,
      paddingVertical: Styles.Spacing.m2,
      paddingHorizontal: Styles.Spacing.m3,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: Styles.white,
    },
    outlinedButtonText: {
      color: Styles.Colors.Canteen.primary,
      fontSize: Styles.Fonts.f1,
      fontWeight: '700',
      fontFamily: Styles.FontFamily.robotoRegular,
    },
    descriptionContainer: {
      marginHorizontal: Styles.Spacing.m3,
    },
    description: {
      fontSize: Styles.Fonts.f8,
      color: Styles.Colors.PayNew.darkHuesBase04,
      marginBottom: Styles.Spacing.m3,
      fontFamily: Styles.FontFamily.robotoRegular,
    },
    customizationSection: {
      flex: 1,
      flexDirection: 'column',
    },
    quantityContainer: {
      marginTop: Styles.Spacing.m0,
    },
    quantitySelectionView: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginHorizontal: Styles.Spacing.m3,
    },
    dashStyle: {
      borderRadius: 100,
      height: PixelRatio.roundToNearestPixel(2),
      marginVertical: Styles.Spacing.m3,
      overflow: 'hidden',
      width: '100%',
    },
    quantityLabel: {
      fontSize: Styles.Fonts.sectionHeader,
      color: Styles.Colors.PayNew.black01,
      fontWeight: '400',
      fontFamily: Styles.FontFamily.robotoRegular,
      marginRight: Styles.Spacing.m3 + Styles.Spacing.m2,
      minHeight: 48,
      marginTop: 10,
    },
  });
}

export function depositStyles(): DepositStylesDescriptor {
  return StyleSheet.create({
    depositView: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: Styles.Spacing.m2,
    },
    transactionItemsDeposit: {
      color: Styles.Colors.PayNew.black01,
      fontFamily: scanAndPayTemplate.textFontFamily,
      fontSize: Styles.Fonts.f1,
    },
  });
}

export function roundedButtonColor() {
  return Styles.primaryColor;
}
export function dashLineColor() {
  return '#999';
}

export function menuListStyles(): MenuListStylesDescriptor {
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    searchContainer: {
      marginLeft: Styles.Spacing.m3,
      marginRight: Styles.Spacing.m3,
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: Styles.Spacing.m3,
      marginTop: Styles.Spacing.m3,
    },
    categories: {
      marginTop: Styles.Spacing.m1,
      paddingBottom: Styles.Spacing.m2 + 2,
    },
  });
}
export function getFlashListProps<T extends Identifiable>({
  data,
  renderItem,
  refreshKey,
}: FlashListPropsType<T>) {
  return {
    data,
    keyExtractor: (item: T) => item.id,
    renderItem,
    ListEmptyComponent: (
      <View style={styles.listEmptyStyles}>
        <AVText
          style={{
            fontSize: Styles.Fonts.f7,
            fontFamily: Styles.FontFamily.robotoRegular,
            fontWeight: '700',
            color: Styles.Colors.PayNew.black01,
          }}
        >
          {Localized.Labels.no_pdt_found}
        </AVText>
        <View style={{alignSelf: 'center'}}>
          <RecentComboIcon
            fillColor={Styles.Colors.Canteen.primary}
            secondaryColor={Styles.Colors.Canteen.lightGreen}
          />
        </View>
      </View>
    ),
    estimatedItemSize: 72,
    extraData: refreshKey,
    ListFooterComponent: <View style={{height: Styles.Heights.h4}} />,
  };
}

export function getProductComponent(props: ProductComponentProps) {
  return (
    <MenuItemListTemplate2
      dnaConfiguration={props?.dnaConfiguration}
      onPress={props?.onPress}
      name={props?.name}
      item={props?.item}
      price={props?.price}
      imageUrl={props?.imageUrl}
      isProductCat={props?.isProductCat}
      index={props?.index}
      key={props?.index}
      overrideDna={props?.overrideDna}
    />
  );
}

export function getTransactionUpsellName(_name: string) {
  return (
    <Text
      style={styles.upsellName}
      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
      aria-label={`${_name}, text`}
      accessibilityLabel-label={`${_name}, text`}
      accessible
      testID="qa_Checkout.UpsellTitle_txt"
      nativeID="qa_Checkout.UpsellTitle_txt"
    >
      {_name}
    </Text>
  );
}

export function getTransactionUpsellProductsList(
  products: MenuProductType[],
  addItem: (item: MenuProductType) => void,
  dna?: DNA,
) {
  return (
    <View style={styles.upsellList}>
      <FlatList
        horizontal
        data={products}
        keyExtractor={(item) => item.id}
        style={styles.flatListStyle}
        contentContainerStyle={styles.containerStyle}
        renderItem={({item}) => (
          <TransactionUpsellItem
            item={item}
            onPress={() => addItem(item)}
            dna={dna}
          />
        )}
      />
    </View>
  );
}

export function renderTenderDiscount(
  _transaction: TransactionDetail | null,
  _props: ListScanItemsProps,
) {
  return null;
}
export function getEditLocationIconColor() {
  return Styles.Colors.Canteen.primary;
}

export function activeCalendarIconColor() {
  return Styles.Colors.PayNew.black01;
}

export function inActiveCalendarIconColor() {
  return Styles.Colors.PayNew.black01;
}

export function getBackgroundColor() {
  return Styles.Colors.Canteen.lightGray;
}

export function getAndroidImageHeight() {
  return Styles.ScreenHeight < 700 ? 470 : 445;
}

export function getDashedLines() {
  return (
    <DashedLine
      dashThickness={PixelRatio.roundToNearestPixel(1)}
      dashGap={PixelRatio.roundToNearestPixel(4)}
      dashLength={PixelRatio.roundToNearestPixel(3)}
      dashColor={dashLineColor()}
      style={styles.dashStyle}
    />
  );
}

export function showTotalAmount(total, props) {
  return (
    <View style={styles.transactionTotalView}>
      <AVText
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel={Localized.Labels.total}
        aria-label={Localized.Labels.total}
        style={styles.transactionTotal}
        nativeID="qa_Total_txt"
      >
        {Localized.Buttons.your_total}
      </AVText>
      <AVFormattedCurrency
        style={styles.transactionTotal}
        value={total}
        accessibilityLabel={'Sub total'}
        currency={AccountStore.getCurrency()}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
        accessible
        testID="qa_TotalValue_txt"
        nativeID="qa_TotalValue_txt"
      />
    </View>
  );
}

export function getAddItemButton(props: AddItemButtonProps) {
  return (
    <View style={styles.addItembtnView}>
      <BarButton
        disabled={props.isDiabled}
        leftText={props.label}
        onPress={props.onPress}
        buttonViewStyle={[
          styles.addBtnStyle,
          {
            backgroundColor: props.isDiabled
              ? Styles.Colors.PayNew.darkHuesBase04
              : Styles.Colors.Canteen.primary,
          },
        ]}
        containerStyle={styles.addBtnContainer}
        rightText={props.rightText}
        textStyle={styles.addBtnText}
        testID="qa_AddItem_btn"
        rightTestID="qa_AddItemAmount_txt"
      />
    </View>
  );
}

export function upsellSelectedBackground() {
  return 'rgba(64, 159, 104, 0.15)';
}

export function upsellSheetStyles() {
  return StyleSheet.create({
    upsellSelectedView: {
      borderWidth: 2,
      borderColor: Styles.Colors.Canteen.primary,
    },
  });
}

export function getUpsellButton(props: UpsellButtonProps) {
  return (
    <View style={styles.getUpsellButton}>
      <BarButton
        disabled={props.isDiabled}
        leftText={props.label}
        onPress={props.onPress}
        buttonViewStyle={[
          styles.addBtnStyle,
          {
            backgroundColor: props.isDiabled
              ? Styles.Colors.PayNew.darkHuesBase04
              : Styles.Colors.Canteen.primary,
            justifyContent: props.isDiabled ? 'center' : 'space-between',
          },
        ]}
        containerStyle={styles.addBtnContainer}
        rightText={!props.isDiabled && props.rightText}
        textStyle={styles.addBtnText}
        rightTestID="qa_UpsellAmount_txt"
        testID="qa_UpdateItem_btn"
      />
    </View>
  );
}

export function getiOSImageHeight() {
  return Styles.ScreenHeight < 700 ? 400 : 440;
}

const styles = StyleSheet.create({
  dashStyle: {
    borderRadius: 100,
    height: PixelRatio.roundToNearestPixel(2),
    marginVertical: Styles.Spacing.m2,
    overflow: 'hidden',
    marginHorizontal: Styles.Spacing.m3,
  },
  transactionTotalView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
  },
  transactionTotal: {
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
    fontFamily: Styles.FontFamily.robotoRegular,
    fontSize: Styles.Fonts.sectionHeader,
  },
  addItembtnView: {
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
    alignSelf: 'center',
  },
  getUpsellButton: {
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Heights.h3,
    marginTop: Styles.Spacing.m3,
    alignSelf: 'center',
  },
  addBtnStyle: {
    width: Platform.OS === 'web' ? '50%' : '100%',
    justifyContent: 'space-between',
    height: Styles.Heights.touchTargetHeight1 + 2,
  },
  addBtnContainer: {
    width: Styles.ScreenWidth,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  addBtnText: {
    fontFamily: Styles.FontFamily.robotoRegular,
    color: Styles.Colors.PayNew.white01,
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
  },
  upsellName: {
    fontFamily: Styles.FontFamily.robotoRegular,
    fontSize: Styles.Fonts.f1,
    color: Styles.Colors.PayNew.black01,
    fontWeight: '700',
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  upsellList: {
    marginVertical: Styles.Spacing.m2,
  },
  flatListStyle: {
    width: '100%',
  },
  nutritionDiscountView: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  nutritionLabelView: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: Styles.Spacing.m3,
  },
  nutritionLabel: {
    color: Styles.Colors.Canteen.green,
    fontFamily: Styles.FontFamily.robotoRegular,
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    marginLeft: Styles.Spacing.m1,
  },
  nutritionPromoAmount: {
    color: Styles.Colors.Canteen.green,
    fontFamily: Styles.FontFamily.robotoRegular,
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
  },
  containerStyle: {paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2},
  listEmptyStyles: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: Styles.Spacing.m3,
    marginTop:
      Platform.OS === 'web' ? Styles.ScreenHeight / 4 : Styles.ScreenWidth / 3,
  },
});

const announcementPopUpButtonColor = '#003349';

export default {
  getPickupLocationStyles,
  getInactiveButtonColor,
  textColor,
  fontFamilyStyle,
  getPickupTimeStyles,
  getActiveButtonColor,
  locationErrorMessage,
  dayErrorMessage,
  timeErrorMessage,
  productDetailsTitle,
  productDetailsImageView,
  productDetailsStyles,
  roundedButtonColor,
  dashLineColor,
  menuListStyles,
  getFlashListProps,
  calorieTextColor,
  getProductComponent,
  getTransactionUpsellName,
  getTransactionUpsellProductsList,
  getEditLocationIconColor,
  activeCalendarIconColor,
  getEmptyCartlblStyle,
  inActiveCalendarIconColor,
  renderTenderDiscount,
  renderColorNutritionComponent,
  renderNutritionPromotionComponent,
  getBackgroundColor,
  getAndroidImageHeight,
  getDashedLines,
  contactPermissionErrorText,
  showTotalAmount,
  getAddItemButton,
  upsellSelectedBackground,
  upsellSheetStyles,
  getUpsellButton,
  getiOSImageHeight,
  announcementPopUpButtonColor,
  depositStyles,
};
